import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {
  Button,
  Divider,
  makeStyles,
  Paper,
  Typography,
} from "@material-ui/core";
import { ThumbUp } from "@material-ui/icons";
import clsx from "clsx";
import Loader from "../../common/Loader/Loader";
import {
  useGetChamberAdminStatsQuery,
  useGetAllChambersQuery,
} from "../../../graphql/types";
import { useEnumStrings } from "../../enumStrings";
import SortableTableCard from "../../common/SortableTableCard";
import MetricWrapper from "../../common/MetricWrapper";
import NonDuesRevenueCalculator from "../../common/NonDuesRevenueCalculator";
import NonDuesRevenueBreakdownTable from "../../common/NonDuesRevenueBreakdownTable";
import { LineChart } from "@mui/x-charts/LineChart";
import { PieChart } from "@mui/x-charts/PieChart";
import LogoImage from "../../common/utilities/LogoImage";
import TipModal from "../../common/TipModal";
import useIsChamber from "../../common/Hooks/useIsChamber";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import ChamberSelector from "./ChamberSelector";
import companiesOnAppIcon from "../../../images/CompaniesOnApp.png";
import perksAvailableIcon from "../../../images/PerksAvailableOnApp.png";
import appPerkViewsIcon from "../../../images/AppPerkViews.png";
import perkGoldHandIcon from "../../../images/perk-gold-hand.png";
import CustomChamberEngageIcon from "../../../images/CustomChamberEngageIcon.png";
import { useLocation } from "react-router-dom";

const goldColor = "#E1A731";
const grayColor = "#DADADA";

const formatDate = ({ utcMilli }: { utcMilli: number }): string => {
  const date = new Date(utcMilli);

  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const month = months[date.getMonth()];
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const hoursStr = String(hours);

  return `${month} ${day}, ${year} ${hoursStr}:${minutes}${ampm}`;
};

type MonthlyChartData = {
  month: string;
  value: number;
};

type MetricCardProps = {
  title: string;
  value?: string;
  highlightBorderColor?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  width?: "25%" | "33%" | "50%" | "66%" | "75%" | "100%";
  icon?: string;
};

type ProgressMetricCardProps = {
  mode?: "percentage" | "number";
  percentage?: number;
  targetPercentage?: number;
  currentValue?: number;
  targetValue?: number;
  maxValue?: number;
  quarterTickValue?: number;
  middleTickValue?: number;
  threeQuarterTickValue?: number;
  ticks: string[];
  ctaLabel: string;
  className?: string;
  ariaLabel: string;
  percentageSuffix: string;
  isLoading?: boolean;
  width?: "25%" | "33%" | "50%" | "66%" | "75%" | "100%";
  xAxisLabel: string;
  subtitle?: string;
  hideValueDisplay?: boolean;
  milestones?: Array<{
    year: number;
    goalValue: number;
  }>;
  targetLabelPrefix?: string;
  idealTargetPercentage?: number;
  idealTargetValue?: number;
};

type DownloadMilestonesStarCardProps = {
  currentValue: number;
  milestones: Array<{
    year: number;
    goalValue: number;
    isFilled: boolean;
    fillPercentage: number;
  }>;
  subscriptionDate?: string | null;
};

const StarIcon = ({
  fillPercentage,
}: {
  fillPercentage: number;
}): ReactElement => {
  const normalizedFill = Math.max(0, Math.min(100, fillPercentage));

  return (
    <svg
      width="50"
      height="50"
      viewBox="0 0 80 76"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "block" }}
    >
      <defs>
        <linearGradient id={`starGradient-${normalizedFill}`}>
          <stop offset={`${normalizedFill}%`} stopColor={goldColor} />
          <stop offset={`${normalizedFill}%`} stopColor={grayColor} />
        </linearGradient>
      </defs>
      <path
        d="M40 0L49.5106 27.6393H78.0423L54.7658 44.7214L64.2765 72.3607L40 55.2786L15.7235 72.3607L25.2342 44.7214L1.95774 27.6393H30.4894L40 0Z"
        fill={`url(#starGradient-${normalizedFill})`}
      />
    </svg>
  );
};

const Tachometer = ({ percentage }: { percentage: number }): ReactElement => {
  // Clamp percentage between 0 and 100
  const clampedPercentage = Math.max(0, Math.min(100, percentage));

  // Convert percentage to angle (0% = -90deg, 100% = 90deg for a 180deg arc)
  const needleAngle = -90 + (clampedPercentage / 100) * 180;

  return (
    <svg viewBox="0 -20 280 180" width="280" height="180">
      {/* Red zone: 0-49% */}
      <path
        d="M 30 130 A 110 110 0 0 1 140 20"
        fill="none"
        stroke="#EF4444"
        strokeWidth="24"
      />
      {/* Green zone: 50-74% */}
      <path
        d="M 140 20 A 110 110 0 0 1 217 52"
        fill="none"
        stroke="#047857"
        strokeWidth="24"
      />
      {/* Gold zone: 75-100% - no strokeLinecap for straight edges */}
      <path
        d="M 217 52 A 110 110 0 0 1 250 130"
        fill="none"
        stroke="#E1A731"
        strokeWidth="24"
      />
      {/* Percentage labels */}
      <text x="15" y="158" fontSize="14" fontWeight="600" fill="#1F2937">
        0%
      </text>
      <text x="130" y="0" fontSize="14" fontWeight="600" fill="#1F2937">
        50%
      </text>
      <text x="232" y="40" fontSize="14" fontWeight="600" fill="#1F2937">
        75%
      </text>
      <text x="240" y="158" fontSize="14" fontWeight="600" fill="#1F2937">
        100%
      </text>
      {/* Needle */}
      <g transform={`rotate(${needleAngle}, 140, 130)`}>
        <line
          x1="140"
          y1="130"
          x2="140"
          y2="40"
          stroke="#1F2937"
          strokeWidth="4"
        />
        <circle cx="140" cy="130" r="10" fill="#1F2937" />
      </g>
    </svg>
  );
};

type StarRowProps = {
  stars: { threshold: number; fillPercentage: number }[];
  showThumbsUp: boolean;
  labelFontSize?: string;
  showIdealTargetText?: boolean;
};

const StarRow = ({
  stars,
  showThumbsUp,
  showIdealTargetText,
}: StarRowProps): ReactElement => {
  const enumStrings = useEnumStrings();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        flexWrap: "wrap",
      }}
    >
      {stars.map((star) => (
        <div
          key={star.threshold}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <StarIcon fillPercentage={star.fillPercentage} />
          <Typography
            style={{
              fontSize: "14px",
              fontWeight: 600,
              marginTop: "4px",
            }}
          >
            {star.threshold}%
          </Typography>
        </div>
      ))}
      {showThumbsUp && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <ThumbUp
            style={{
              fontSize: "48px",
              color: goldColor,
              marginLeft: "8px",
            }}
          />
          {showIdealTargetText && (
            <Typography
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginTop: "4px",
                textAlign: "center",
              }}
            >
              {enumStrings.rootChamberAdmin.idealTargetReached}
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

type MemberDashboardLoginsStarCardProps = {
  title: string;
  value: string;
  starBreakdown?: {
    firstRow: { threshold: number; fillPercentage: number }[];
    secondRow: { threshold: number; fillPercentage: number }[];
    showFirstRowThumbsUp: boolean;
    showSecondRowThumbsUp: boolean;
  };
  isLoading?: boolean;
};

const MemberDashboardLoginsStarCard = ({
  title,
  value,
  starBreakdown,
  isLoading,
}: MemberDashboardLoginsStarCardProps): ReactElement => {
  const classes = useStyles();

  return (
    <Paper elevation={0} className={classes.metricCard}>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <Loader size={80} fullPage={false} />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            overflow: "hidden",
          }}
        >
          <Typography component="p" className={classes.metricValue}>
            {value}
          </Typography>
          <Typography component="h3" className={classes.metricTitle}>
            {title}
          </Typography>
          {starBreakdown && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: "16px",
              }}
            >
              <StarRow
                stars={starBreakdown.firstRow}
                showThumbsUp={starBreakdown.showFirstRowThumbsUp}
                showIdealTargetText={true}
              />
              {starBreakdown.showFirstRowThumbsUp &&
                starBreakdown.secondRow.length > 0 && (
                  <StarRow
                    stars={starBreakdown.secondRow}
                    showThumbsUp={starBreakdown.showSecondRowThumbsUp}
                    showIdealTargetText={true}
                  />
                )}
            </div>
          )}
        </div>
      )}
    </Paper>
  );
};

const DownloadMilestonesStarCard = ({
  currentValue,
  milestones,
  subscriptionDate,
}: DownloadMilestonesStarCardProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  // Format subscription date to show inception month/year
  const getInceptionText = (): string => {
    if (!subscriptionDate) return "";
    const date = new Date(subscriptionDate);
    const months = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <Paper elevation={0} className={classes.metricCard}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "16px",
          width: "100%",
        }}
      >
        <Typography
          component="h3"
          style={{ fontSize: "19px", color: "#3C3C43" }}
        >
          {enumStrings.rootChamberAdmin.updatedDashboard.cumulativeMemberDownloadsSince.replace(
            "{{inceptionDate}}",
            getInceptionText()
          )}
        </Typography>
        <Typography
          component="p"
          style={{ fontSize: "38px", fontWeight: 700, color: "#111827" }}
        >
          {currentValue.toLocaleString()}
        </Typography>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          {milestones.map((milestone, index) => (
            <div
              key={milestone.year}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                flex: "0 1 auto",
              }}
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    padding: "4px 8px",
                    borderRadius: "999px",
                    backgroundColor: "#047857",
                    color: "#FFFFFF",
                    fontSize: "15px",
                    fontWeight: 700,
                  }}
                >
                  {milestone.goalValue.toLocaleString()}
                </div>
                <div
                  style={{
                    width: 0,
                    marginTop: -1,
                    height: 0,
                    borderLeft: "7px solid transparent",
                    borderRight: "7px solid transparent",
                    borderTop: "10px solid #047857",
                  }}
                />
              </div>
              <StarIcon fillPercentage={milestone.fillPercentage} />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0px",
                }}
              >
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {enumStrings.rootChamberAdmin.updatedDashboard.year}
                </Typography>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {milestone.year}
                </Typography>
              </div>
            </div>
          ))}
        </div>
        <Typography
          style={{
            fontSize: "14px",
            color: "#6B7280",
            textAlign: "center",
            lineHeight: "1.5",
          }}
        >
          {
            enumStrings.rootChamberAdmin.updatedDashboard.metrics
              .memberUserAccounts.starDiagramDescription
          }
        </Typography>
      </div>
    </Paper>
  );
};

type PerkMilestonesStarCardProps = {
  currentValue: number;
  milestones: Array<{
    year: number;
    goalValue: number;
    isFilled: boolean;
    fillPercentage: number;
  }>;
  subscriptionDate?: string | null;
  title: string;
  isLoading?: boolean;
};

const PerkMilestonesStarCard = ({
  currentValue,
  milestones,
  subscriptionDate,
  title,
  isLoading,
}: PerkMilestonesStarCardProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  return (
    <Paper elevation={0} className={classes.metricCard}>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <Loader size={80} fullPage={false} />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
          }}
        >
          <Typography
            component="p"
            style={{ fontSize: "38px", fontWeight: 700, color: "#111827" }}
          >
            {currentValue.toLocaleString()}
          </Typography>
          <Typography
            component="h3"
            style={{ fontSize: "19px", color: "#3C3C43" }}
          >
            {title}
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            {milestones.map((milestone, index) => (
              <div
                key={milestone.year}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  flex: "0 1 auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      padding: "4px 8px",
                      borderRadius: "999px",
                      backgroundColor: "#047857",
                      color: "#FFFFFF",
                      fontSize: "15px",
                      fontWeight: 700,
                    }}
                  >
                    {milestone.goalValue.toLocaleString()}
                  </div>
                  <div
                    style={{
                      width: 0,
                      marginTop: -1,
                      height: 0,
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderTop: "10px solid #047857",
                    }}
                  />
                </div>
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {enumStrings.rootChamberAdmin.updatedDashboard.year}
                </Typography>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {milestone.year}
                </Typography>
              </div>
            ))}
          </div>
          <Typography
            style={{
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
              lineHeight: "1.5",
            }}
          >
            {
              enumStrings.rootChamberAdmin.updatedDashboard
                .perkMilestonesDescription
            }
          </Typography>
        </div>
      )}
    </Paper>
  );
};

type MembersOfferingPerksMilestonesStarCardProps = {
  currentValue: number;
  currentPercentage: number;
  milestones: Array<{
    year: number;
    goalPercentage: number;
    isFilled: boolean;
    fillPercentage: number;
  }>;
  subscriptionDate?: string | null;
  title: string;
  isLoading?: boolean;
};

const MembersOfferingPerksMilestonesStarCard = ({
  currentValue,
  currentPercentage,
  milestones,
  subscriptionDate,
  title,
  isLoading,
}: MembersOfferingPerksMilestonesStarCardProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  return (
    <Paper elevation={0} className={classes.metricCard}>
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <Loader size={80} fullPage={false} />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            width: "100%",
          }}
        >
          <Typography
            component="p"
            style={{ fontSize: "38px", fontWeight: 700, color: "#111827" }}
          >
            {currentValue.toLocaleString()} ({currentPercentage}%)
          </Typography>
          <Typography
            component="h3"
            style={{ fontSize: "19px", color: "#3C3C43" }}
          >
            {title}
          </Typography>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            {milestones.map((milestone) => (
              <div
                key={milestone.year}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                  flex: "0 1 auto",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      padding: "4px 8px",
                      borderRadius: "999px",
                      backgroundColor: "#047857",
                      color: "#FFFFFF",
                      fontSize: "15px",
                      fontWeight: 700,
                    }}
                  >
                    {milestone.goalPercentage}%
                  </div>
                  <div
                    style={{
                      width: 0,
                      marginTop: -1,
                      height: 0,
                      borderLeft: "7px solid transparent",
                      borderRight: "7px solid transparent",
                      borderTop: "10px solid #047857",
                    }}
                  />
                </div>
                <Typography
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {enumStrings.rootChamberAdmin.updatedDashboard.year}
                </Typography>
                <Typography
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    color: "#1F2937",
                  }}
                >
                  {milestone.year}
                </Typography>
              </div>
            ))}
          </div>
          <Typography
            style={{
              fontSize: "14px",
              color: "#6B7280",
              textAlign: "center",
              lineHeight: "1.5",
            }}
          >
            {
              enumStrings.rootChamberAdmin.updatedDashboard
                .membersOfferingPerksMilestonesDescription
            }
          </Typography>
        </div>
      )}
    </Paper>
  );
};

const DiscountBreakDownPieChart = ({
  numOfAllMemberPerks,
  numofLocalMemberOnlyPerks,
  numOfGeneralPublicPerks,
  numOfCompanyEmployeesOnlyPerks,
}: {
  numOfAllMemberPerks: number;
  numofLocalMemberOnlyPerks: number;
  numOfGeneralPublicPerks: number;
  numOfCompanyEmployeesOnlyPerks: number;
}) => {
  const enumStrings = useEnumStrings();

  if (
    numOfAllMemberPerks === 0 &&
    numofLocalMemberOnlyPerks === 0 &&
    numOfGeneralPublicPerks === 0 &&
    numOfCompanyEmployeesOnlyPerks === 0
  ) {
    return (
      <div
        style={{
          display: "flex",
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
          fontSize: "20px",
        }}
      >
        {enumStrings.rootAdminChamberText.createPerkToSeeChart}
      </div>
    );
  }

  const totalPerks =
    numOfAllMemberPerks +
    numofLocalMemberOnlyPerks +
    numOfGeneralPublicPerks +
    numOfCompanyEmployeesOnlyPerks;

  const calculatePercentage = ({ value }: { value: number }): string => {
    if (totalPerks === 0) return "0.0";
    return ((value / totalPerks) * 100).toFixed(1);
  };

  const legendColors = [goldColor, "#73C6B6", "#E89BA4", "#8B5CF6"];

  const legendData = [
    {
      label: enumStrings.rootChamberAdmin.anyone,
      value: numOfGeneralPublicPerks,
      color: legendColors[0],
    },
    {
      label: enumStrings.rootChamberAdmin.allMembers,
      value: numOfAllMemberPerks,
      color: legendColors[1],
    },
    {
      label: enumStrings.rootChamberAdmin.localMembers,
      value: numofLocalMemberOnlyPerks,
      color: legendColors[2],
    },
    {
      label: enumStrings.rootChamberAdmin.memberCompany,
      value: numOfCompanyEmployeesOnlyPerks,
      color: legendColors[3],
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <PieChart
        series={[
          {
            valueFormatter: (item) =>
              `${calculatePercentage({ value: item.value })}% (${item.value})`,
            innerRadius: 50,
            outerRadius: 100,
            data: [
              {
                id: 0,
                value: numOfGeneralPublicPerks,
                label: enumStrings.rootChamberAdmin.anyone,
                color: legendColors[0],
              },
              {
                id: 1,
                value: numOfAllMemberPerks,
                label: enumStrings.rootChamberAdmin.allMembers,
                color: legendColors[1],
              },
              {
                id: 2,
                value: numofLocalMemberOnlyPerks,
                label: enumStrings.rootChamberAdmin.localMembers,
                color: legendColors[2],
              },
              {
                id: 3,
                value: numOfCompanyEmployeesOnlyPerks,
                label: enumStrings.rootChamberAdmin.memberCompany,
                color: legendColors[3],
              },
            ],
          },
        ]}
        margin={{ left: 20, bottom: 20, top: 20, right: 20 }}
        slotProps={{
          legend: { hidden: true },
        }}
        height={240}
      />
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "12px",
            paddingLeft: "20px",
            paddingRight: "20px",
          }}
        >
          {legendData.map(({ label, value, color }) => (
            <div
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                fontSize: "16px",
                gap: "16px",
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <div
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor: color,
                    flexShrink: 0,
                  }}
                />
                <span>{label}</span>
              </div>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <span style={{ fontWeight: 700 }}>
                  {calculatePercentage({ value })}%
                </span>
                {label === enumStrings.rootChamberAdmin.anyone && (
                  <span
                    style={{
                      backgroundColor: "#047857",
                      color: "white",
                      padding: "4px 12px",
                      borderRadius: "999px",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {enumStrings.rootChamberAdmin.target} (75%)
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  page: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "100%",
    padding: theme.spacing(4, 3, 6),
    boxSizing: "border-box",
    backgroundColor: "#FFFFFF",
    overflowX: "hidden",
  },
  headingGroup: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    backgroundColor: goldColor,
    padding: theme.spacing(3),
  },
  headingTitle: {
    fontSize: "24px",
    fontWeight: 700,
    color: "white",
  },
  headingDescription: {
    fontSize: "16px",
    color: "white",
    maxWidth: "720px",
  },
  metricsGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    width: "100%",
    alignItems: "stretch",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  metricsGridCentered: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    width: "100%",
    alignItems: "stretch",
    justifyContent: "center",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
    },
  },
  stackedMetricsWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    boxSizing: "border-box",
    flex: "0 0 auto",
    height: "100%",
    [theme.breakpoints.down("sm")]: {
      width: "100% !important",
    },
  },
  metricCard: {
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(3),
    minHeight: 160,
    height: "100%",
    borderRadius: 24,
    boxShadow: "0px 0px 10px 1px lightgrey",
    border: "5px solid transparent",
  },
  metricCardHighlighted: {
    borderColor: "#F5C84C",
  },
  metricTitle: {
    fontSize: "19px",
    color: "#3C3C43",
  },
  metricValue: {
    fontSize: "38px",
    fontWeight: 700,
    color: "#111827",
  },
  progressWrapper: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3.5),
    alignItems: "stretch",
    width: "100%",
  },
  progressBarContainer: {
    position: "relative",
    width: "100%",
    height: 32,
    borderRadius: 999,
    boxShadow: "0 6px 12px darkgrey",
    overflow: "visible",
  },
  progressIndicator: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    zIndex: 2,
    transition: "width 0.4s ease",
    borderRadius: 999,
  },
  progressTarget: {
    position: "absolute",
    bottom: "100%",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    zIndex: 3,
    pointerEvents: "none",
  },
  progressTargetMarker: {
    width: 0,
    marginTop: -1,
    height: 0,
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderTop: "10px solid #047857",
    zIndex: 100,
  },
  progressTargetLabel: {
    padding: theme.spacing(0.25, 1.25),
    borderRadius: 999,
    backgroundColor: "#047857",
    color: "#FFFFFF",
    fontSize: "16px",
    fontWeight: 700,
  },
  progressEdgeMarker: {
    position: "absolute",
    bottom: -14,
    width: 2,
    height: 18,
    borderRadius: 999,
    backgroundColor: "#1F2937",
    zIndex: 2,
  },
  progressEdgeStart: {
    left: 0,
    transform: "translateX(-50%)",
  },
  progressEdgeEnd: {
    right: 0,
    transform: "translateX(50%)",
  },
  progressTicks: {
    position: "relative",
    fontSize: "14px",
    color: "#1F2937",
    fontWeight: 600,
    width: "100%",
    height: 40,
  },
  progressTickLabel: {
    position: "absolute",
    transform: "translateX(-50%)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  progressTickLabelStart: {
    left: "0%",
    transform: "translateX(0)",
    alignItems: "flex-start",
  },
  progressTickLabelEnd: {
    left: "100%",
    transform: "translateX(-100%)",
    alignItems: "flex-end",
  },
  progressTickMark: {
    width: 1,
    height: 18,
    borderRadius: 999,
    backgroundColor: "#1F2937",
  },
  progressXAxisLabel: {
    fontSize: "16px",
    color: "#1F2937",
    fontWeight: 600,
    textAlign: "center",
    marginTop: theme.spacing(1),
  },
  percentageDisplay: {
    fontSize: "38px",
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
  },
  loaderWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },

  progressCurrentBubble: {
    position: "relative",
    color: "#FFFFFF",
    fontWeight: 700,
    fontSize: "14px",
    padding: theme.spacing(0.5, 1.75),
    borderRadius: 12,
    boxShadow: "0px 0px 6px darkgrey",
  },
  progressCurrentConnector: {
    width: 0,
    height: 8,
    backgroundColor: "transparent",
  },

  ctaButton: {
    alignSelf: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "12px",
    borderRadius: 15,
    padding: theme.spacing(1.25, 2),
    backgroundColor: goldColor,
    color: "white",
    boxShadow: "0px 0px 10px 1px lightgrey",
    marginLeft: "20px",
    "&:hover": {
      alignSelf: "flex-start",
      textTransform: "none",
      fontWeight: 700,
      fontSize: "12px",
      borderRadius: 15,
      padding: theme.spacing(1.25, 2),
      backgroundColor: goldColor,
      color: "white",
      boxShadow: "0px 0px 10px 1px lightgrey",
      marginLeft: "20px",
    },
  },
  pageInnerHeading: {
    fontSize: "22px",
    fontWeight: 700,
    marginTop: "-5px",
    marginBottom: "0px",
  },
  sectionDivider: {
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
  },
  chartContainer: {
    width: "100%",
    height: 350,
    marginTop: -theme.spacing(2),
  },
}));

const MetricCard = ({
  title,
  value,
  highlightBorderColor,
  children,
  isLoading,
  className,
  icon,
}: Omit<MetricCardProps, "width">): ReactElement => {
  const classes = useStyles();

  return (
    <Paper
      elevation={0}
      className={clsx(classes.metricCard, className, {
        [classes.metricCardHighlighted]: Boolean(highlightBorderColor),
      })}
      style={{ borderColor: highlightBorderColor }}
    >
      {isLoading ? (
        <div className={classes.loaderWrapper}>
          <Loader size={80} fullPage={false} />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            {value ? (
              <Typography component="p" className={classes.metricValue}>
                {value}
              </Typography>
            ) : null}
            {icon && (
              <img
                src={icon}
                alt=""
                style={{
                  height: "56px",
                  width: "auto",
                  flexShrink: 0,
                }}
              />
            )}
          </div>
          <Typography component="h3" className={classes.metricTitle}>
            {title}
          </Typography>
          {children}
        </div>
      )}
    </Paper>
  );
};

type LineChartCardProps = {
  title: string;
  chartData: MonthlyChartData[];
  chartSeriesLabel: string;
  chartXAxisLabel: string;
  chartYAxisLabel: string;
  isLoading?: boolean;
  width?: "25%" | "33%" | "50%" | "66%" | "75%" | "100%";
  averageValue?: number;
  hideLegend?: boolean;
};

const LineChartCard = ({
  title,
  chartData,
  chartSeriesLabel,
  chartXAxisLabel,
  chartYAxisLabel,
  isLoading,
  width,
  averageValue,
  hideLegend,
}: LineChartCardProps): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  // Calculate max value from the data, including average line
  const maxValue = useMemo(() => {
    if (!chartData || chartData.length === 0) return 0;
    const dataMax = Math.max(...chartData.map((d) => d.value));
    // Include average value in max calculation to ensure it's visible
    return averageValue !== undefined
      ? Math.max(dataMax, averageValue)
      : dataMax;
  }, [chartData, averageValue]);

  return (
    <MetricWrapper width={width}>
      <MetricCard title={title} isLoading={isLoading}>
        {!isLoading && chartData && chartData.length > 0 && (
          <div className={classes.chartContainer}>
            <LineChart
              xAxis={[
                {
                  scaleType: "point",
                  data: chartData.map((d) => d.month),
                  label: chartXAxisLabel,
                },
              ]}
              yAxis={[
                {
                  min: 0,
                  max: maxValue,
                  label: chartYAxisLabel,
                },
              ]}
              series={[
                {
                  data: chartData.map((d) => d.value),
                  color: goldColor,
                  curve: "linear",
                  label: chartSeriesLabel,
                },
                ...(averageValue !== undefined && averageValue > 0
                  ? [
                      {
                        data: chartData.map(() => averageValue),
                        color: "#9E9E9E",
                        curve: "linear" as const,
                        label:
                          enumStrings.rootChamberAdmin.updatedDashboard
                            .averageDownloads,
                        showMark: false,
                      },
                    ]
                  : []),
              ]}
              margin={{ left: 70, right: 60, top: 60, bottom: 70 }}
              sx={{
                // Tick labels - set to 14px
                "& .MuiChartsAxis-tickLabel": {
                  fontSize: "14px !important",
                },
                "& .MuiChartsAxis-tick text": {
                  fontSize: "14px !important",
                },
                "& text": {
                  fontSize: "14px !important",
                },
                // Axis labels - set to 16px (these come after to override generic text rule)
                "& .MuiChartsAxis-left .MuiChartsAxis-label": {
                  transform: "translateX(-20px)",
                  fontSize: "16px !important",
                },
                "& .MuiChartsAxis-bottom .MuiChartsAxis-label": {
                  transform: "translateY(15px)",
                  fontSize: "16px !important",
                },
                "& .MuiChartsAxis-label": {
                  fontSize: "16px !important",
                },
                "& .MuiChartsAxis-label text": {
                  fontSize: "16px !important",
                },
                "& .MuiChartsAxis-directionY text[class*='label']": {
                  fontSize: "16px !important",
                },
                "& .MuiChartsAxis-directionX text[class*='label']": {
                  fontSize: "16px !important",
                },
              }}
              slotProps={{
                legend: {
                  hidden:
                    hideLegend ??
                    (averageValue === undefined || averageValue === 0),
                },
              }}
            />
          </div>
        )}
      </MetricCard>
    </MetricWrapper>
  );
};

const ProgressMetricCard = ({
  title,
  mode = "percentage",
  percentage,
  targetPercentage,
  currentValue,
  targetValue,
  maxValue,
  quarterTickValue,
  middleTickValue,
  threeQuarterTickValue,
  ticks,
  ctaLabel,
  onTipClick,
  className,
  ariaLabel,
  percentageSuffix,
  isLoading,
  width,
  xAxisLabel,
  subtitle,
  hideValueDisplay,
  milestones,
  targetLabelPrefix,
  idealTargetPercentage,
  idealTargetValue,
}: ProgressMetricCardProps & {
  title: string;
  onTipClick: () => void;
}): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings();

  if (isLoading) {
    return (
      <MetricWrapper width={width}>
        <MetricCard title={title} isLoading className={className} />
      </MetricWrapper>
    );
  }

  // Calculate percentages based on mode
  const normalizedPercentage =
    mode === "number" && currentValue !== undefined && maxValue !== undefined
      ? Math.max(0, Math.min((currentValue / maxValue) * 100, 100))
      : Math.max(0, Math.min(percentage ?? 0, 100));

  const normalizedTarget =
    mode === "number" && targetValue !== undefined && maxValue !== undefined
      ? Math.max(0, Math.min((targetValue / maxValue) * 100, 100))
      : Math.max(0, Math.min(targetPercentage ?? 0, 100));

  // Generate display value based on mode
  const displayValue =
    mode === "number" && currentValue !== undefined
      ? currentValue.toLocaleString()
      : `${Math.round(normalizedPercentage)}`;

  const displaySuffix = mode === "number" ? "" : percentageSuffix;

  const targetDisplayValue =
    mode === "number" && targetValue !== undefined
      ? targetValue.toLocaleString()
      : `${targetPercentage}${percentageSuffix}`;

  let mainValue =
    mode === "percentage" && currentValue !== undefined
      ? `${currentValue.toLocaleString()} (${Math.round(
          normalizedPercentage
        )}${displaySuffix})`
      : `${displayValue}${displaySuffix}`;

  // Generate ticks based on mode
  const displayTicks =
    mode === "number" && maxValue !== undefined
      ? [
          "0",
          quarterTickValue?.toLocaleString() ?? "",
          middleTickValue?.toLocaleString() ?? "",
          threeQuarterTickValue?.toLocaleString() ?? "",
          maxValue.toLocaleString(),
        ]
      : ticks;

  const primaryColor = goldColor;
  const secondaryColor = grayColor;

  // Calculate edge-aware transform for target bubble to prevent text wrapping
  const getTargetTransform = ({
    targetPercent,
  }: {
    targetPercent: number;
  }): string => {
    if (targetPercent < 15) return "translateX(33%)";
    if (targetPercent > 85) return "translateX(-33%)";
    return "translateX(0)";
  };

  return (
    <MetricWrapper width={width}>
      <MetricCard title={title} className={className}>
        <div className={classes.progressWrapper}>
          {!hideValueDisplay && (
            <Typography component="p" className={classes.percentageDisplay}>
              {mainValue}
            </Typography>
          )}
          <div
            style={{
              marginTop: hideValueDisplay
                ? milestones && milestones.length > 0
                  ? "60px"
                  : "48px"
                : "0px",
            }}
          >
            <div
              className={classes.progressBarContainer}
              role="progressbar"
              aria-label={ariaLabel}
              aria-valuenow={normalizedPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              style={{
                background: secondaryColor,
              }}
            >
              <div
                className={classes.progressIndicator}
                style={{
                  width: `${normalizedPercentage}%`,
                  background: primaryColor,
                }}
              />
              {/* Only show target bubble if no milestones AND target is defined */}
              {(!milestones || milestones.length === 0) &&
              (mode === "number"
                ? targetValue !== undefined
                : targetPercentage !== undefined) ? (
                <div
                  className={classes.progressTarget}
                  style={{
                    left: `${normalizedTarget}%`,
                    width: "100%",
                  }}
                >
                  <span
                    className={classes.progressTargetLabel}
                    style={{
                      transform: getTargetTransform({
                        targetPercent: normalizedTarget,
                      }),
                    }}
                  >
                    {targetLabelPrefix ?? enumStrings.rootChamberAdmin.target} (
                    {targetDisplayValue})
                  </span>
                  <div className={classes.progressTargetMarker} />
                </div>
              ) : null}
              {milestones &&
                milestones.length > 0 &&
                milestones.map((milestone, index) => {
                  const position = maxValue
                    ? (milestone.goalValue / maxValue) * 100
                    : 0;

                  // Adjust transform based on position to prevent overflow
                  const getBubbleTransform = (): string => {
                    if (position < 15) return "translateX(-20%)";
                    if (position > 85) return "translateX(-80%)";
                    return "translateX(-50%)";
                  };

                  return (
                    <div
                      key={milestone.year}
                      style={{
                        position: "absolute",
                        left: `${position}%`,
                        bottom: "100%",
                        transform: getBubbleTransform(),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        zIndex: 3,
                        pointerEvents: "none",
                      }}
                    >
                      <div
                        style={{
                          padding: "4px 8px",
                          borderRadius: "12px",
                          backgroundColor: "#047857",
                          textAlign: "center",
                          color: "#FFFFFF",
                          fontSize: "16px",
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                        }}
                      >
                        {
                          <span>
                            {enumStrings.rootChamberAdmin.updatedDashboard.milestoneTargetLabel.replace(
                              "{{year}}",
                              String(milestone.year)
                            )}
                            <br />({milestone.goalValue.toLocaleString()})
                          </span>
                        }
                      </div>
                      <div
                        style={{
                          width: 0,
                          height: 0,
                          marginTop: "-1px",
                          marginLeft:
                            position > 85
                              ? "60%"
                              : position < 15
                              ? "-60%"
                              : "0",
                          borderLeft: "7px solid transparent",
                          borderRight: "7px solid transparent",
                          borderTop: `10px solid #047857`,
                        }}
                      />
                    </div>
                  );
                })}
              {/* Fixed Ideal Target Bubble */}
              {idealTargetPercentage !== undefined &&
                (() => {
                  // For number mode, calculate position based on idealTargetValue / maxValue
                  // For percentage mode, use idealTargetPercentage directly
                  const idealTargetPosition =
                    mode === "number" &&
                    idealTargetValue !== undefined &&
                    maxValue !== undefined &&
                    maxValue > 0
                      ? Math.max(
                          0,
                          Math.min((idealTargetValue / maxValue) * 100, 100)
                        )
                      : idealTargetPercentage;

                  return (
                    <div
                      className={classes.progressTarget}
                      style={{
                        left: `${idealTargetPosition}%`,
                        width: "100%",
                      }}
                    >
                      <span
                        className={classes.progressTargetLabel}
                        style={{
                          transform: getTargetTransform({
                            targetPercent: idealTargetPosition,
                          }),
                        }}
                      >
                        {enumStrings.rootChamberAdmin.idealTarget} (
                        {mode === "number" && idealTargetValue !== undefined
                          ? idealTargetValue.toLocaleString()
                          : `${idealTargetPercentage}${percentageSuffix}`}
                        )
                      </span>
                      <div className={classes.progressTargetMarker} />
                    </div>
                  );
                })()}
            </div>
            <div className={classes.progressTicks}>
              {displayTicks.map((label, index) => {
                const tickPercentage =
                  (index / (displayTicks.length - 1)) * 100;
                return (
                  <span
                    key={`${label}-${index}`}
                    className={clsx(classes.progressTickLabel, {
                      [classes.progressTickLabelStart]: index === 0,
                      [classes.progressTickLabelEnd]:
                        index === displayTicks.length - 1,
                    })}
                    style={{
                      left:
                        index === 0 || index === displayTicks.length - 1
                          ? undefined
                          : `${tickPercentage}%`,
                    }}
                  >
                    <div className={classes.progressTickMark} />
                    <span>{label}</span>
                  </span>
                );
              })}
            </div>
            <Typography className={classes.progressXAxisLabel}>
              {xAxisLabel}
            </Typography>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Button className={classes.ctaButton} onClick={onTipClick}>
              {ctaLabel}
            </Button>
          </div>
          {subtitle && (
            <Typography
              style={{
                textAlign: "center",
                fontSize: "16px",
                fontWeight: 500,
                color: "#6B7280",
              }}
            >
              {subtitle}
            </Typography>
          )}
        </div>
      </MetricCard>
    </MetricWrapper>
  );
};

const UpdatedRootChamberAdmin = (): ReactElement => {
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash;

    // Function to scroll to the element
    const scrollToElement = () => {
      if (hash) {
        const element = document.getElementById("placeToScrollToChamberEngage");
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    // Set timeout to delay the execution
    const timeoutId = setTimeout(scrollToElement, 500);

    // Clear the timeout if the component unmounts or location changes
    return () => clearTimeout(timeoutId);
  }, [location]);

  const classes = useStyles();
  const enumStrings = useEnumStrings();

  // Get current user's chamber info
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const SPECIAL_CHAMBER_ID = "2fa7868a-7ec6-4e4d-8b32-2da14fc51be6";
  const isSpecialChamber = chamberInfo?.id === SPECIAL_CHAMBER_ID;

  // Additional chambers to include in selector
  const additionalChambers = [
    { id: "2fa7868a-7ec6-4e4d-8b32-2da14fc51be6", name: "Sand Bay Chamber" },
  ];

  // State for selected chamber
  const [selectedChamberId, setSelectedChamberId] = useState<string | null>(
    null
  );

  // Initialize selectedChamberId to user's own chamberId when component mounts
  useEffect(() => {
    if (chamberInfo?.id && selectedChamberId === null) {
      setSelectedChamberId(chamberInfo.id);
    }
  }, [chamberInfo?.id, selectedChamberId]);

  // Fetch all chambers for the selector (only if special chamber)
  const { data: allChambersData } = useGetAllChambersQuery({
    skip: !isSpecialChamber,
  });

  const { data, loading } = useGetChamberAdminStatsQuery({
    variables: { chamberId: selectedChamberId || undefined },
    skip: !selectedChamberId,
  });
  const stats = data?.getChamberAdminStats;

  const updatedDashboard = enumStrings.rootChamberAdmin.updatedDashboard;

  const [tipModalOpen, setTipModalOpen] = useState(false);
  const [currentTipContent, setCurrentTipContent] = useState("");
  const [targetName, setTargetName] = useState("");

  const handleOpenTip = ({
    content,
    title,
  }: {
    content: string;
    title: string;
  }) => {
    setCurrentTipContent(content);
    setTargetName(title);
    setTipModalOpen(true);
  };

  const handleCloseTip = () => {
    setTipModalOpen(false);
  };

  const membersOnAppValue = useMemo(() => {
    if (!stats?.totalChamberMembersOnApp) {
      return "0";
    }
    return stats.totalChamberMembersOnApp.toLocaleString();
  }, [stats?.totalChamberMembersOnApp]);

  const totalMembersFeaturedValue = useMemo(() => {
    if (!stats?.totalMemberCompaniesFeaturedOnAppSystemWide) {
      return "0";
    }
    return stats.totalMemberCompaniesFeaturedOnAppSystemWide.toLocaleString();
  }, [stats?.totalMemberCompaniesFeaturedOnAppSystemWide]);

  const isChamber = useIsChamber();

  const chamberOrBoardText = isChamber
    ? enumStrings.chamber
    : enumStrings.board;

  const progressProps: ProgressMetricCardProps & {
    title: string;
    onTipClick: () => void;
  } = useMemo(() => {
    const metric = stats?.membersWithNewPerkNotificationsEnabled;
    return {
      title: updatedDashboard.metrics.notificationsEnabled.title,
      percentage: metric?.currentPercentage,
      targetPercentage: 50,
      targetLabelPrefix: enumStrings.rootChamberAdmin.idealTarget,
      ticks: updatedDashboard.progressTicks,
      ctaLabel: updatedDashboard.metrics.notificationsEnabled.ctaLabel,
      onTipClick: () =>
        handleOpenTip({
          content: updatedDashboard.metrics.notificationsEnabled.tipContent,
          title: updatedDashboard.metrics.notificationsEnabled.title,
        }),
      ariaLabel:
        updatedDashboard.metrics.notificationsEnabled.progressAriaLabel,
      percentageSuffix: updatedDashboard.progressPercentageSuffix,
      isLoading: loading,
      xAxisLabel: updatedDashboard.metrics.notificationsEnabled.xAxisLabel,
      subtitle: "",
    };
  }, [
    loading,
    stats?.membersWithNewPerkNotificationsEnabled,
    updatedDashboard,
    enumStrings.rootChamberAdmin.idealTarget,
  ]);

  const notificationsPercentageValue = useMemo(() => {
    const currentPercentage =
      stats?.membersWithNewPerkNotificationsEnabled?.currentPercentage;
    if (currentPercentage === undefined || currentPercentage === null) {
      return "0%";
    }
    return `${Math.round(currentPercentage)}%`;
  }, [stats?.membersWithNewPerkNotificationsEnabled?.currentPercentage]);

  const memberUserAccountsProgressProps: ProgressMetricCardProps & {
    title: string;
    onTipClick: () => void;
  } = useMemo(() => {
    const metric = stats?.membersWhoDownloadedAppAndSetUpAccount;
    const milestones = stats?.downloadMilestones || [];

    // Use highest milestone goalValue as maxValue to ensure all milestones fit on bar
    const maxMilestoneValue =
      milestones.length > 0
        ? Math.max(...milestones.map((m) => m.goalValue))
        : metric?.maxValue || 0;

    const quarterTickValue = maxMilestoneValue
      ? Math.floor(maxMilestoneValue / 4)
      : 0;
    const middleTickValue = maxMilestoneValue
      ? Math.floor(maxMilestoneValue / 2)
      : 0;
    const threeQuarterTickValue = maxMilestoneValue
      ? Math.floor((maxMilestoneValue * 3) / 4)
      : 0;

    return {
      mode: "number",
      title: updatedDashboard.metrics.memberUserAccounts.title,
      currentValue: metric?.currentValue,
      targetValue: metric?.targetValue,
      maxValue: maxMilestoneValue,
      quarterTickValue,
      middleTickValue,
      threeQuarterTickValue,
      ticks: [],
      ctaLabel: updatedDashboard.metrics.memberUserAccounts.ctaLabel,
      onTipClick: () =>
        handleOpenTip({
          content: updatedDashboard.metrics.memberUserAccounts.tipContent,
          title: updatedDashboard.metrics.memberUserAccounts.title,
        }),
      ariaLabel: updatedDashboard.metrics.memberUserAccounts.progressAriaLabel,
      percentageSuffix: updatedDashboard.progressPercentageSuffix,
      isLoading: loading,
      xAxisLabel: updatedDashboard.metrics.memberUserAccounts.xAxisLabel,
      subtitle: "",
      milestones: milestones.map((m) => ({
        year: m.year,
        goalValue: m.goalValue,
      })),
    };
  }, [
    loading,
    stats?.membersWhoDownloadedAppAndSetUpAccount,
    stats?.downloadMilestones,
    updatedDashboard,
  ]);

  const memberDownloadChartData: MonthlyChartData[] = useMemo(() => {
    const metric = stats?.membersWhoDownloadedAppAndSetUpAccount;

    // Format month from "YYYY-MM" to "MMM YYYY"
    const formatMonthYear = ({
      monthAndYear,
    }: {
      monthAndYear: string;
    }): string => {
      const [year, month] = monthAndYear.split("-");
      const monthNames = [
        enumStrings.monthsOfYearAbbreviated.jan,
        enumStrings.monthsOfYearAbbreviated.feb,
        enumStrings.monthsOfYearAbbreviated.mar,
        enumStrings.monthsOfYearAbbreviated.apr,
        enumStrings.monthsOfYearAbbreviated.may,
        enumStrings.monthsOfYearAbbreviated.jun,
        enumStrings.monthsOfYearAbbreviated.jul,
        enumStrings.monthsOfYearAbbreviated.aug,
        enumStrings.monthsOfYearAbbreviated.sep,
        enumStrings.monthsOfYearAbbreviated.oct,
        enumStrings.monthsOfYearAbbreviated.nov,
        enumStrings.monthsOfYearAbbreviated.dec,
      ];
      const monthIndex = parseInt(month, 10) - 1;
      return `${monthNames[monthIndex]} ${year}`;
    };

    // Transform monthly data into chart format
    return (
      metric?.monthlyData?.map((item) => ({
        month: formatMonthYear({ monthAndYear: item.monthAndYear }),
        value: item.appSignups,
      })) || []
    );
  }, [stats?.membersWhoDownloadedAppAndSetUpAccount, enumStrings]);

  const memberDashboardLoginsValue = useMemo(() => {
    const currentValue =
      stats?.membersWhoLoggedIntoMemberDashboard?.currentValue;
    const currentPercentage =
      stats?.membersWhoLoggedIntoMemberDashboard?.currentPercentage;

    if (!currentValue) {
      return "0";
    }

    const formattedValue = currentValue.toLocaleString();

    if (currentPercentage !== undefined && currentPercentage !== null) {
      const roundedPercentage = Math.round(currentPercentage);
      return `${formattedValue} (${roundedPercentage}%)`;
    }

    return formattedValue;
  }, [stats?.membersWhoLoggedIntoMemberDashboard]);

  const memberDashboardLoginsProgressProps: ProgressMetricCardProps & {
    title: string;
    onTipClick: () => void;
  } = useMemo(() => {
    const metric = stats?.membersWhoLoggedIntoMemberDashboard;

    return {
      mode: "percentage" as const,
      title: updatedDashboard.metrics.memberDashboardLogins.title,
      percentage: metric?.currentPercentage,
      percentParentValue: metric?.currentValue,
      targetPercentage: 20,
      targetLabelPrefix: enumStrings.rootChamberAdmin.idealTarget,
      ticks: updatedDashboard.progressTicks,
      ctaLabel: updatedDashboard.metrics.memberDashboardLogins.ctaLabel,
      onTipClick: () =>
        handleOpenTip({
          content: updatedDashboard.metrics.memberDashboardLogins.tipContent,
          title: updatedDashboard.metrics.memberDashboardLogins.title,
        }),
      ariaLabel:
        updatedDashboard.metrics.memberDashboardLogins.progressAriaLabel,
      percentageSuffix: updatedDashboard.progressPercentageSuffix,
      isLoading: loading,
      xAxisLabel: updatedDashboard.metrics.memberDashboardLogins.xAxisLabel,
      subtitle: "",
    };
  }, [
    loading,
    stats?.membersWhoLoggedIntoMemberDashboard,
    updatedDashboard,
    enumStrings.rootChamberAdmin.idealTarget,
  ]);

  const chamberPerksOfferedProgressProps: ProgressMetricCardProps & {
    title: string;
    onTipClick: () => void;
  } = useMemo(() => {
    const metric = stats?.numberOfPerksOffered;
    const targetVal = metric?.targetValue || 0;
    // Use ideal target value for max, with rounding to nearest 10
    const idealTarget = metric?.idealTargetValue || targetVal;
    const maxVal = Math.ceil(idealTarget / 10) * 10;
    const quarterTickValue = Math.ceil(maxVal / 4 / 10) * 10;
    const middleTickValue = Math.ceil(maxVal / 2 / 10) * 10;
    const threeQuarterTickValue = Math.ceil((maxVal * 3) / 4 / 10) * 10;

    return {
      mode: "number",
      title: updatedDashboard.metrics.chamberPerksOffered.title,
      currentValue: metric?.currentValue,
      // Don't show dynamic target bubble - only show ideal target
      targetValue: undefined,
      maxValue: maxVal,
      quarterTickValue,
      middleTickValue,
      threeQuarterTickValue,
      ticks: [],
      ctaLabel: updatedDashboard.metrics.chamberPerksOffered.ctaLabel,
      onTipClick: () =>
        handleOpenTip({
          content: updatedDashboard.metrics.chamberPerksOffered.tipContent,
          title: updatedDashboard.metrics.chamberPerksOffered.title,
        }),
      ariaLabel: updatedDashboard.metrics.chamberPerksOffered.progressAriaLabel,
      percentageSuffix: updatedDashboard.progressPercentageSuffix,
      isLoading: loading,
      xAxisLabel: updatedDashboard.metrics.chamberPerksOffered.xAxisLabel,
      subtitle: "",
      idealTargetPercentage: metric?.idealTargetPercentage,
      idealTargetValue: metric?.idealTargetValue,
    };
  }, [loading, stats?.numberOfPerksOffered, updatedDashboard]);

  const totalPerksOfferedValue = useMemo(() => {
    if (!stats?.totalPerksOfferedOnAppSystemWide) {
      return "0";
    }
    return stats.totalPerksOfferedOnAppSystemWide.toLocaleString();
  }, [stats?.totalPerksOfferedOnAppSystemWide]);

  const totalPerkViewsValue = useMemo(() => {
    if (!stats?.totalPerkViewsOnAppSystemWide) {
      return "0";
    }
    return stats.totalPerkViewsOnAppSystemWide.toLocaleString();
  }, [stats?.totalPerkViewsOnAppSystemWide]);

  const numberOfMembersOfferingPerksProgressProps: ProgressMetricCardProps & {
    title: string;
    onTipClick: () => void;
  } = useMemo(() => {
    const metric = stats?.numberOfMembersOfferingPerks;
    return {
      mode: "percentage",
      title: updatedDashboard.metrics.numberOfMembersOfferingPerks.title,
      percentage: metric?.currentPercentage,
      // Don't show dynamic target bubble - only show ideal target
      targetPercentage: undefined,
      currentValue: metric?.currentValue,
      targetValue: metric?.targetValue,
      ticks: updatedDashboard.progressTicks,
      ctaLabel: updatedDashboard.metrics.numberOfMembersOfferingPerks.ctaLabel,
      onTipClick: () =>
        handleOpenTip({
          content:
            updatedDashboard.metrics.numberOfMembersOfferingPerks.tipContent,
          title: updatedDashboard.metrics.numberOfMembersOfferingPerks.title,
        }),
      ariaLabel:
        updatedDashboard.metrics.numberOfMembersOfferingPerks.progressAriaLabel,
      percentageSuffix: updatedDashboard.progressPercentageSuffix,
      isLoading: loading,
      xAxisLabel:
        updatedDashboard.metrics.numberOfMembersOfferingPerks.xAxisLabel,
      subtitle: "",
      idealTargetPercentage: metric?.idealTargetPercentage,
      idealTargetValue: metric?.idealTargetValue,
    };
  }, [loading, stats?.numberOfMembersOfferingPerks, updatedDashboard]);

  const perkViewsValue = useMemo(() => {
    if (!stats?.perkViewKPI?.currentValue) {
      return "0";
    }
    return stats.perkViewKPI.currentValue.toLocaleString();
  }, [stats?.perkViewKPI?.currentValue]);

  const companyLeadsData = useMemo(() => {
    return stats?.companyLeads || [];
  }, [stats?.companyLeads]);

  const sortedMostPopularPerks = useMemo(() => {
    if (!stats?.mostPopularPerks) return [];
    return [...stats.mostPopularPerks].sort(
      (a, b) => (b.views || 0) - (a.views || 0)
    );
  }, [stats?.mostPopularPerks]);

  const sortedNewestPerks = useMemo(() => {
    if (!stats?.newestPerks) return [];
    return [...stats.newestPerks].sort(
      (a, b) => (b.createdAtUtcMilli || 0) - (a.createdAtUtcMilli || 0)
    );
  }, [stats?.newestPerks]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <header className={classes.headingGroup}>
        <Typography component="h1" className={classes.headingTitle}>
          {updatedDashboard.headingTitle}
        </Typography>
        <Typography component="p" className={classes.headingDescription}>
          {updatedDashboard.headingDescription}
        </Typography>
      </header>
      {isSpecialChamber && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            backgroundColor: "#FFFFFF",
            paddingTop: "20px",
          }}
        >
          <ChamberSelector
            chamberId={selectedChamberId}
            setChamberId={setSelectedChamberId}
            allChamberData={allChambersData}
            customLabel={
              enumStrings.rootChamberAdmin.updatedDashboard
                .selectAnotherChamberStats
            }
            additionalChambers={additionalChambers}
          />
        </div>
      )}
      <section className={classes.page}>
        <Typography className={classes.pageInnerHeading}>
          {updatedDashboard.chamberPerksAppOverview}
        </Typography>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MetricCard
              title={updatedDashboard.metrics.totalMembersFeatured.title}
              value={totalMembersFeaturedValue}
              highlightBorderColor="#F5C84C"
              isLoading={loading}
              icon={companiesOnAppIcon}
            />
          </MetricWrapper>
          <MetricWrapper width="33%">
            <MetricCard
              title={updatedDashboard.metrics.totalPerksOffered.title}
              value={totalPerksOfferedValue}
              highlightBorderColor="#F5C84C"
              isLoading={loading}
              icon={perksAvailableIcon}
            />
          </MetricWrapper>
          <MetricWrapper width="33%">
            <MetricCard
              title={updatedDashboard.metrics.totalPerkViews.title}
              value={totalPerkViewsValue}
              highlightBorderColor="#F5C84C"
              isLoading={loading}
              icon={appPerkViewsIcon}
            />
          </MetricWrapper>
          <MetricWrapper width="100%">
            <SortableTableCard
              title={updatedDashboard.metrics.companyLeads.title}
              subtitle={updatedDashboard.metrics.companyLeads.subtitle}
              columns={[
                {
                  id: "name",
                  label: enumStrings.rootChamberAdmin.companyName,
                  sortType: "string",
                },
                {
                  id: "createdAtUtcMilli",
                  label: enumStrings.rootChamberAdmin.submissionDate,
                  sortType: "number",
                },
              ]}
              data={companyLeadsData}
              isLoading={loading}
              defaultSortColumn="createdAtUtcMilli"
              defaultSortDirection="desc"
              formatCell={(columnId, value) => {
                if (
                  columnId === "createdAtUtcMilli" &&
                  typeof value === "number"
                ) {
                  return formatDate({ utcMilli: value });
                }
                return value;
              }}
              style={{ height: "100%", maxHeight: 525 }}
            />
          </MetricWrapper>
        </div>
        <Divider className={classes.sectionDivider} />
        <Typography className={classes.pageInnerHeading}>
          {updatedDashboard.memberEngagement}
        </Typography>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MetricCard
              title={updatedDashboard.metrics.membersOnApp.title.replace(
                "{{chamberOrBoard}}",
                chamberOrBoardText
              )}
              value={membersOnAppValue}
              isLoading={loading}
            />
          </MetricWrapper>
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            {loading ? (
              <MetricCard
                title={
                  enumStrings.rootChamberAdmin.updatedDashboard
                    .cumulativeMemberDownloadsTitle
                }
                isLoading={loading}
              />
            ) : (
              <DownloadMilestonesStarCard
                currentValue={
                  stats?.membersWhoDownloadedAppAndSetUpAccount?.currentValue ||
                  0
                }
                milestones={stats?.downloadMilestones || []}
                subscriptionDate={stats?.subscriptionDate || null}
              />
            )}
          </MetricWrapper>
          <ProgressMetricCard
            {...memberUserAccountsProgressProps}
            title={
              updatedDashboard.metrics.memberUserAccounts.twelveMonthTargetTitle
            }
            width="66%"
            hideValueDisplay
          />
        </div>
        <div className={classes.metricsGrid}>
          <LineChartCard
            hideLegend={true}
            title={
              updatedDashboard.metrics.memberUserAccounts.downloadHistoryTitle
            }
            chartData={memberDownloadChartData}
            chartSeriesLabel={
              updatedDashboard.metrics.memberUserAccounts.chartYAxisLabel
            }
            chartXAxisLabel={
              updatedDashboard.metrics.memberUserAccounts.chartXAxisLabel
            }
            chartYAxisLabel={
              updatedDashboard.metrics.memberUserAccounts.chartYAxisLabel
            }
            isLoading={loading}
            width="100%"
            averageValue={
              stats?.membersWhoDownloadedAppAndSetUpAccount
                ?.averageMonthlySignups
            }
          />
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MemberDashboardLoginsStarCard
              title={updatedDashboard.metrics.memberDashboardLogins.title}
              value={memberDashboardLoginsValue}
              starBreakdown={
                stats?.membersWhoLoggedIntoMemberDashboard?.starBreakdown
              }
              isLoading={loading}
            />
          </MetricWrapper>
          <ProgressMetricCard
            {...memberDashboardLoginsProgressProps}
            title={updatedDashboard.metrics.memberDashboardLogins.twelveMonthTargetTitle.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText
            )}
            width="66%"
            hideValueDisplay
          />
        </div>
        <Divider className={classes.sectionDivider} />
        <Typography className={classes.pageInnerHeading}>
          {updatedDashboard.perkEngagement}
        </Typography>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MetricCard
              title={updatedDashboard.metrics.perkViews.title.replace(
                "{{chamberOrBoard}}",
                chamberOrBoardText
              )}
              value={perkViewsValue}
              isLoading={loading}
              icon={perkGoldHandIcon}
            />
          </MetricWrapper>
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MetricCard
              title={progressProps.title}
              value={notificationsPercentageValue}
              isLoading={loading}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  marginTop: "16px",
                }}
              >
                <Tachometer
                  percentage={
                    stats?.membersWithNewPerkNotificationsEnabled
                      ?.currentPercentage ?? 0
                  }
                />
              </div>
            </MetricCard>
          </MetricWrapper>
          <ProgressMetricCard
            {...progressProps}
            title={updatedDashboard.metrics.notificationsEnabled.targetProgressTitle.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText
            )}
            width="66%"
            hideValueDisplay={true}
          />
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <PerkMilestonesStarCard
              title={updatedDashboard.metrics.chamberPerksOffered.title}
              currentValue={stats?.numberOfPerksOffered?.currentValue || 0}
              milestones={stats?.perkMilestones || []}
              subscriptionDate={stats?.subscriptionDate}
              isLoading={loading}
            />
          </MetricWrapper>
          <ProgressMetricCard
            {...chamberPerksOfferedProgressProps}
            title={updatedDashboard.metrics.chamberPerksOffered.targetProgressTitle.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText
            )}
            width="66%"
            hideValueDisplay={true}
          />
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="33%">
            <MembersOfferingPerksMilestonesStarCard
              title={
                updatedDashboard.metrics.numberOfMembersOfferingPerks.title
              }
              currentValue={
                stats?.numberOfMembersOfferingPerks?.currentValue || 0
              }
              currentPercentage={
                stats?.numberOfMembersOfferingPerks?.currentPercentage || 0
              }
              milestones={stats?.memberOfferingPerksMilestones || []}
              subscriptionDate={stats?.subscriptionDate}
              isLoading={loading}
            />
          </MetricWrapper>
          <ProgressMetricCard
            {...numberOfMembersOfferingPerksProgressProps}
            title={updatedDashboard.metrics.numberOfMembersOfferingPerks.targetProgressTitle.replace(
              "{{chamberOrBoard}}",
              chamberOrBoardText
            )}
            width="66%"
            hideValueDisplay={true}
          />
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="50%">
            <MetricCard
              title={
                enumStrings.rootChamberAdmin.updatedDashboard.metrics
                  .perkRedeemableByBreakdown.title
              }
              isLoading={loading}
            >
              {!loading && (
                <DiscountBreakDownPieChart
                  numOfAllMemberPerks={
                    stats?.perkRedeemableByBreakdown?.numOfAllMemberPerks || 0
                  }
                  numofLocalMemberOnlyPerks={
                    stats?.perkRedeemableByBreakdown
                      ?.numOfLocalMemberOnlyPerks || 0
                  }
                  numOfGeneralPublicPerks={
                    stats?.perkRedeemableByBreakdown?.numOfGeneralPublicPerks ||
                    0
                  }
                  numOfCompanyEmployeesOnlyPerks={
                    stats?.perkRedeemableByBreakdown
                      ?.numOfCompanyEmployeesOnlyPerks || 0
                  }
                />
              )}
            </MetricCard>
          </MetricWrapper>
          <MetricWrapper width="50%">
            <SortableTableCard
              title={updatedDashboard.metrics.perksCategoryBreakdown.title}
              columns={[
                {
                  id: "categoryName",
                  label:
                    updatedDashboard.metrics.perksCategoryBreakdown.columns
                      .category,
                  sortType: "string",
                },
                {
                  id: "numberOfMembers",
                  label:
                    updatedDashboard.metrics.perksCategoryBreakdown.columns
                      .numberOfMembers,
                  sortType: "number",
                },
                {
                  id: "numberOfPerks",
                  label:
                    updatedDashboard.metrics.perksCategoryBreakdown.columns
                      .numberOfPerks,
                  sortType: "number",
                },
                {
                  id: "actualPercentage",
                  label:
                    updatedDashboard.metrics.perksCategoryBreakdown.columns
                      .percentOfPerks,
                  sortType: "number",
                },
              ]}
              data={stats?.perksCategoryBreakdown || []}
              isLoading={loading}
              defaultSortColumn="numberOfPerks"
              sortByLabel={
                updatedDashboard.metrics.perksCategoryBreakdown.sortByLabel
              }
              noDataMessage={
                updatedDashboard.metrics.perksCategoryBreakdown.noDataAvailable
              }
              formatCell={(columnId, value) => {
                if (
                  columnId === "actualPercentage" &&
                  typeof value === "number"
                ) {
                  return `${value.toFixed(1)}%`;
                }
                return value;
              }}
            />
          </MetricWrapper>
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="50%">
            <SortableTableCard
              title={enumStrings.rootAdminChamberText.mostPopularPerks.replace(
                "{{chamberOrBoard}}",
                chamberOrBoardText
              )}
              columns={[
                {
                  id: "companyName",
                  label: enumStrings.rootAdminChamberText.perkTableCompanyName,
                  sortType: "string",
                },
                {
                  id: "perkTitle",
                  label: enumStrings.rootAdminChamberText.perkTablePerkTitle,
                  sortType: "string",
                },
                {
                  id: "offeredTo",
                  label: enumStrings.rootAdminChamberText.perkTableOfferedTo,
                  sortType: "string",
                },
                {
                  id: "views",
                  label: enumStrings.rootAdminChamberText.perkTableViews,
                  sortType: "number",
                },
              ]}
              data={sortedMostPopularPerks}
              isLoading={loading}
              noDataMessage={enumStrings.rootAdminChamberText.noPerksAvailable}
              formatCell={(columnId, value, row) => {
                if (columnId === "companyName") {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <LogoImage
                        size="smallNormal"
                        logoSource={row.companyLogo}
                        chamberName={value}
                        squared
                      />
                      <div style={{ width: "15px" }} />
                      <span>{value}</span>
                    </div>
                  );
                }
                return value;
              }}
            />
          </MetricWrapper>
          <MetricWrapper width="50%">
            <SortableTableCard
              title={enumStrings.rootAdminChamberText.newestPerks.replace(
                "{{chamberOrBoard}}",
                chamberOrBoardText
              )}
              columns={[
                {
                  id: "companyName",
                  label: enumStrings.rootAdminChamberText.perkTableCompanyName,
                  sortType: "string",
                },
                {
                  id: "perkTitle",
                  label: enumStrings.rootAdminChamberText.perkTablePerkTitle,
                  sortType: "string",
                },
                {
                  id: "offeredTo",
                  label: enumStrings.rootAdminChamberText.perkTableOfferedTo,
                  sortType: "string",
                },
              ]}
              data={sortedNewestPerks}
              isLoading={loading}
              noDataMessage={enumStrings.rootAdminChamberText.noPerksAvailable}
              formatCell={(columnId, value, row) => {
                if (columnId === "companyName") {
                  return (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <LogoImage
                        size="smallNormal"
                        logoSource={row.companyLogo}
                        chamberName={value}
                        squared
                      />
                      <div style={{ width: "15px" }} />
                      <span>{value}</span>
                    </div>
                  );
                }
                return value;
              }}
            />
          </MetricWrapper>
        </div>
        <Divider className={classes.sectionDivider} />
        <div className={classes.pageInnerHeading}>
          <img
            id="placeToScrollToChamberEngage"
            alt="CustomChamberEngageIcon"
            src={CustomChamberEngageIcon}
            style={{
              maxWidth: "250px",
              objectFit: "contain",
            }}
          />
        </div>
        <div className={classes.metricsGrid}>
          <MetricWrapper width="100%">
            <NonDuesRevenueCalculator
              totalMembers={stats?.totalChamberMembersOnApp || 0}
              chamberOrBoardText={chamberOrBoardText}
              isLoading={loading}
            />
          </MetricWrapper>
          <MetricWrapper width="100%">
            <NonDuesRevenueBreakdownTable
              data={stats?.nonDuesRevenueBreakdown}
              chamberOrBoardText={chamberOrBoardText}
              isLoading={loading}
            />
          </MetricWrapper>
        </div>
      </section>
      <TipModal
        open={tipModalOpen}
        onClose={handleCloseTip}
        title={updatedDashboard.tipModalTitle.replace(
          "{{targetName}}",
          targetName
        )}
        content={currentTipContent}
      />
    </>
  );
};

export default UpdatedRootChamberAdmin;
